import type { ResourceListerItem } from "../types/lister.ts";
import type { PermissionType } from "../types/permission.ts";

export const PREVIEW_ACCESS_TYPE_MESSAGE = `This component may have limited features or support. Please contact support if you have any questions`;

export const AwsIconUrl =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fc/Amazon_Web_Services_2025.svg/960px-Amazon_Web_Services_2025.svg.png";
export const AwsLambdaIconUrl =
  "https://upload.wikimedia.org/wikipedia/commons/5/5c/Amazon_Lambda_architecture_logo.svg";
export const AwsRdsIconUrl =
  "https://a.b.cdn.console.awsstatic.com/a/v1/6JW3RZSXOZ4EFR2HZBU6IIOCUZEIFKRK6JZDCUDHQVQXRMAORYWQ/icon/1d374ed2a6bcf601d7bfd4fc3dfd3b5d-c9f69416d978016b3191175f35e59226.svg";
export const AzureIconUrl =
  "https://upload.wikimedia.org/wikipedia/commons/f/fa/Microsoft_Azure.svg";
export const DuoIconUrl =
  "https://guide.duo.com/static/images/duo-security.png";
export const CloudflareIconUrl = "https://www.cloudflare.com/favicon.ico";
export const CopilotStudioIconUrl =
  "https://copilotstudio.microsoft.com/favicon.ico";
export const DatadogIconUrl =
  "https://imgix.datadoghq.com/img/dd_logo_70x75.png";
export const CustomIconUrl =
  "https://raw.githubusercontent.com/ant-design/ant-design-icons/refs/heads/master/packages/icons-svg/svg/outlined/setting.svg";
export const EntraIdIconUrl =
  "https://learn.microsoft.com/en-us/entra/media/index/microsoft-entra-id-color.png";
export const GcloudIconUrl =
  "https://lh3.googleusercontent.com/VEnnK2SyklusfxZ3dIYjlQH3xSwK2BFSJ69TFQ9g8HjM6m3CouRlTia5FW3z3GS0x83WC9TylZCaA9Jf_2kmr7mXxI9_HYLZTFy_bg";
// See usage restrictions at https://github.com/logos
export const GithubIconUrl =
  "https://github.githubassets.com/assets/GitHub-Mark-ea2971cee799.png";
export const GoogleWorkspaceIconUrl =
  "https://fonts.gstatic.com/s/i/productlogos/admin_2020q4/v6/192px.svg";
export const IncidentIoIconUrl = "https://incident.io/favicon.ico";
export const K8sIconUrl =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/3/39/Kubernetes_logo_without_workmark.svg/250px-Kubernetes_logo_without_workmark.svg.png";
export const JiraIconUrl =
  "https://wac-cdn.atlassian.com/dam/jcr:fa01756d-6dcc-45d1-83ab-696fbfeb074f/Jira-icon-blue.svg?cdnVersion=1571";
export const OktaIconUrl = "https://www.okta.com/favicon.ico";
export const OracleIconUrl =
  "https://upload.wikimedia.org/wikipedia/commons/e/e1/Oracle_Corporation_logo.svg";
export const McpIconUrl =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fe/Model_Context_Protocol_logo.svg/3840px-Model_Context_Protocol_logo.svg.png";
export const MysqlIconUrl =
  "https://www.mysql.com/common/logos/logo-mysql-170x115.png";
export const PagerdutyIconUrl = "https://pagerduty.com/favicon.ico";
export const PingIconUrl =
  "https://www.pingidentity.com/etc.clientlibs/settings/wcm/designs/pic6/assets/resources/images/favicon.ico";
export const PostgresIconUrl =
  "https://wiki.postgresql.org/images/a/a4/PostgreSQL_logo.3colors.svg";
export const SnowflakeIconUrl =
  "https://avatars.githubusercontent.com/u/6453780?s=200&v=4";
// This image is in the public domain See https://commons.wikimedia.org/wiki/File:Terminalicon2.png
export const RdpIconUrl =
  "https://upload.wikimedia.org/wikipedia/commons/7/75/RemoteDesktopManager-Icon-HR.png";
export const SshIconUrl =
  "https://upload.wikimedia.org/wikipedia/commons/b/b3/Terminalicon2.png";
export const SplunkIconUrl =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1d/Splunk_logo.svg/3840px-Splunk_logo.svg.png";
export const TailscaleIconUrl = "https://tailscale.com/favicon.ico";
export const CiscoIconUrl = "https://www.cisco.com/favicon.ico";
export const WebexIconUrl =
  "https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/webex.svg";
export const ZscalerIconUrl = "https://www.zscaler.com/favicon.ico";
export const SelfHostedIconUrl =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/5/56/Black_computer_icon.png/960px-Black_computer_icon.png";

export const WindowsIconUrl =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0a/Unofficial_Windows_logo_variant_-_2002%E2%80%932012_%28Multicolored%29.svg/960px-Unofficial_Windows_logo_variant_-_2002%E2%80%932012_%28Multicolored%29.svg.png";

export const PERMISSION_INTEGRATION_LOGO_SOURCE: Readonly<
  Record<string, string>
> = {
  "aws-rds": AwsRdsIconUrl,
  "azure-ad": EntraIdIconUrl,
  "cisco-secure-access": CiscoIconUrl,
  "entra-id": EntraIdIconUrl,
  "self-hosted": SelfHostedIconUrl,
  "splunk-stack": SplunkIconUrl,
  "zscaler-private-access": ZscalerIconUrl,
  aws: AwsIconUrl,
  azure: AzureIconUrl,
  custom: CustomIconUrl,
  gcloud: GcloudIconUrl,
  github: GithubIconUrl,
  k8s: K8sIconUrl,
  mcp: McpIconUrl,
  mysql: MysqlIconUrl,
  oci: OracleIconUrl,
  okta: OktaIconUrl,
  pg: PostgresIconUrl,
  rdp: RdpIconUrl,
  snowflake: SnowflakeIconUrl,
  ssh: SshIconUrl,
  tailscale: TailscaleIconUrl,
  windows: WindowsIconUrl,
  workspace: GoogleWorkspaceIconUrl,
} satisfies Record<PermissionType, string>;

export const INTEGRATION_RESOURCE_LOGO_SOURCE: Readonly<
  Record<string, (item: ResourceListerItem) => string | undefined>
> = {
  aws: (item) => {
    if (!("group" in item) || !item.group) return undefined;
    const [service] = item.group.split(":");
    // Icons are sourced from https://aws.amazon.com/architecture/icons/ and are wholly owned by Amazon Web Services, Inc. Please refer to the icons page for terms of use.
    switch (service) {
      case "s3":
        return "https://icon.icepanel.io/AWS/svg/Storage/Simple-Storage-Service.svg";
      case "iam":
        return "https://icon.icepanel.io/AWS/svg/Security-Identity-Compliance/Identity-and-Access-Management.svg";
      case "sqs":
        return "https://icon.icepanel.io/AWS/svg/App-Integration/Simple-Queue-Service.svg";
      case "lambda":
        return "https://icon.icepanel.io/AWS/svg/Compute/Lambda.svg";
      case "ec2":
        return "https://icon.icepanel.io/AWS/svg/Compute/EC2.svg";
      case "ecr":
        return "https://icon.icepanel.io/AWS/svg/Containers/Elastic-Container-Registry.svg";
      case "rds":
        return "https://icon.icepanel.io/AWS/svg/Database/RDS.svg";
      case "elasticloadbalancing":
        return "https://icon.icepanel.io/AWS/svg/Networking-Content-Delivery/Elastic-Load-Balancing.svg";
      case "dynamodb":
        return "https://icon.icepanel.io/AWS/svg/Database/DynamoDB.svg";
      case "cloudwatch":
        return "https://icon.icepanel.io/AWS/svg/Management-Governance/CloudWatch.svg";
      case "cloudfront":
        return "https://icon.icepanel.io/AWS/svg/Networking-Content-Delivery/CloudFront.svg";
      case "sns":
        return "https://icon.icepanel.io/AWS/svg/Messaging/Simple-Notification-Service.svg";
      case "logs":
        return "https://icon.icepanel.io/AWS/svg/Management-Governance/CloudTrail.svg";
      case "eks":
        return "https://icon.icepanel.io/AWS/svg/Containers/Elastic-Kubernetes-Service.svg";
      case "ecs":
        return "https://icon.icepanel.io/AWS/svg/Containers/Elastic-Container-Service.svg";
      case "sagemaker":
        return "https://icon.icepanel.io/AWS/svg/Machine-Learning/SageMaker.svg";
      default:
        return AwsIconUrl;
    }
  },
};
